import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import { useForgotPassword } from "@/hooks/useForgotPassword"
import { isValidEmail } from "@/helpers/isValidEmail"

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPassword
})

function ForgotPassword() {
  const navigate = useNavigate()
  const { email, setEmail, passwordResetSuccessful, isSubmitting, submit } = useForgotPassword()
  const canSubmit = isValidEmail(email) && !isSubmitting

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!canSubmit || passwordResetSuccessful) return
    submit()
  }

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        bgcolor: "secondary.light",
        alignItems: "center",
        justifyContent: "center",
        p: 2
      }}
    >
      <Stack
        component="form"
        onSubmit={handleSubmit}
        gap={2}
        sx={{
          width: 300,
          py: 3,
          px: 2,
          borderRadius: 1,
          boxShadow: 1,
          bgcolor: "background.paper"
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <img src="/legalware.png" width={100} height={70} alt="legalware company logo" />
          <Typography variant="h3" color="secondary">
            legalware
          </Typography>
        </div>

        {passwordResetSuccessful ? (
          <>
            <Typography variant="h5" sx={{ textAlign: "center" }}>
              Check your inbox
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
              We sent a password reset link to <strong>{email.trim()}</strong>. Follow the link to
              set a new password.
            </Typography>
            <Button variant="contained" onClick={() => navigate({ to: "/auth" })}>
              Back to login
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" sx={{ textAlign: "center" }}>
              Forgot your password?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
              Enter the email tied to your account and we&apos;ll send you a reset link.
            </Typography>
            <TextField
              id="email"
              type="email"
              label="Email"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Send reset link"}
            </Button>
            <Typography
              variant="button"
              component={Link}
              to="/auth"
              sx={{ textDecoration: "none", textAlign: "center" }}
            >
              Back to login
            </Typography>
          </>
        )}
      </Stack>
    </Stack>
  )
}
